<template>
  <div class="price-periods">
    <div class="d-flex justify-content-between align-items-center flex-wrap mb-2">
      <h5 class="mb-0">{{ $t('pricePeriods.title') }}</h5>
      <div class="mt-2 mt-sm-0">
        <CButton color="secondary" variant="outline" class="mr-2" @click="openSchedule">
          <i class="far fa-calendar-alt mr-1"></i> {{ $t('priceSchedule.title') }}
        </CButton>
        <CButton color="primary" @click="openAdd">
          <i class="fas fa-plus mr-1"></i> {{ $t('pricePeriods.add') }}
        </CButton>
      </div>
    </div>
    <p class="text-muted small mb-3">{{ $t('pricePeriods.help') }}</p>

    <div v-if="loadError" class="alert alert-danger">{{ loadError }}</div>

    <div v-if="loading" class="text-center py-3">
      <CSpinner color="primary" size="sm" />
    </div>
    <div v-else-if="!periods.length && !loadError" class="price-periods-empty">
      {{ $t('pricePeriods.empty') }}
    </div>
    <div v-else-if="periods.length" class="table-responsive">
      <table class="table table-bordered table-sm price-periods-table mb-0">
        <thead>
          <tr>
            <th>{{ $t('pricePeriods.name') }}</th>
            <th>{{ $t('pricePeriods.dates') }}</th>
            <th>{{ $t('experiences.price') }}</th>
            <th class="text-right">{{ $t('actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(period, index) in periods" :key="period.id">
            <td>
              <span class="period-swatch" :style="{ background: colorOf(index).solid }"></span>
              <strong>{{ period.name }}</strong>
            </td>
            <td class="text-nowrap">
              <template v-if="isSingleDay(period)">
                {{ formatDateDMY(period.validFrom) }} <span class="badge badge-light ml-1">{{ $t('pricePeriods.singleDay') }}</span>
              </template>
              <template v-else>
                {{ formatDateDMY(period.validFrom) }} – {{ formatDateDMY(period.validTo) }}
              </template>
              <!-- The website only shows a period's price on dates that have time slots -->
              <div v-if="coverage[period.id]" class="period-coverage">
                <small v-if="coverage[period.id].ended" class="text-muted">{{ $t('pricePeriods.ended') }}</small>
                <small v-else-if="coverage[period.id].failed" class="text-muted">{{ $t('pricePeriods.slotsCheckFailed') }}</small>
                <small v-else-if="coverage[period.id].days > 0" class="text-success">
                  <i class="fas fa-check mr-1"></i>{{ $t('pricePeriods.slotDays', { count: coverage[period.id].days }) }}
                </small>
                <div v-else class="period-no-slots">
                  <i class="fas fa-exclamation-triangle mr-1"></i>
                  {{ $t('pricePeriods.noSlots', { from: formatDateDMY(coverage[period.id].from), to: formatDateDMY(coverage[period.id].to) }) }}
                  <a :href="createSlotsHref(period)" target="_blank" rel="noopener" class="d-inline-block ml-1">
                    {{ $t('pricePeriods.createSlots') }} <i class="fas fa-external-link-alt"></i>
                  </a>
                </div>
              </div>
            </td>
            <td>
              <template v-if="priceType === 'PerPerson'">
                <span v-for="bracket in pricedAgeBrackets(period.ageBracketPrices)" :key="bracket.key" class="bracket-price">
                  {{ bracket.label }}: <strong>{{ formatBracketPrice(bracket.price) }}</strong>
                </span>
                <span v-if="!pricedAgeBrackets(period.ageBracketPrices).length">{{ formatEuro(period.price) }}</span>
                <small v-else-if="keepsSomeBasePrices(period)" class="text-muted d-block">{{ $t('pricePeriods.otherBracketsBase') }}</small>
              </template>
              <strong v-else>{{ formatEuro(period.price) }}</strong>
            </td>
            <td class="text-right text-nowrap">
              <CButton color="primary" variant="outline" size="sm" class="mr-2" @click="openEdit(period)">{{ $t('edit') }}</CButton>
              <CButton color="danger" variant="outline" size="sm" @click="openDelete(period)">{{ $t('remove') }}</CButton>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Add / edit period -->
    <CModal :show.sync="showForm" :title="editing ? $t('pricePeriods.editTitle') : $t('pricePeriods.addTitle')" color="primary" :close-on-backdrop="false">
      <div v-if="formErrors.length" class="alert alert-danger" role="alert">
        <div v-for="(message, index) in formErrors" :key="index">{{ message }}</div>
      </div>
      <div class="form-group">
        <label>{{ $t('pricePeriods.name') }}*</label>
        <input type="text" class="form-control" v-model="form.name" :placeholder="$t('pricePeriods.namePlaceholder')" maxlength="100">
      </div>
      <div class="form-row">
        <div class="form-group col-sm-6">
          <label>{{ $t('pricePeriods.validFrom') }}*</label>
          <input type="date" class="form-control" v-model="form.validFrom">
        </div>
        <div class="form-group col-sm-6">
          <label>{{ $t('pricePeriods.validTo') }}*</label>
          <input type="date" class="form-control" v-model="form.validTo" :min="form.validFrom">
        </div>
      </div>
      <div v-if="priceType === 'PerPerson'" class="form-group">
        <label>{{ $t('experiences.ageBracketPrices') }}</label>
        <table class="table table-bordered mb-0">
          <thead>
            <tr>
              <th>{{ $t('experiences.ageBracket') }}</th>
              <th>{{ $t('experiences.price') }} (€)</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="key in ageBracketKeys" :key="key">
              <td>{{ ageBracketLabel(key) }}</td>
              <td>
                <input type="number" step="0.01" min="0" class="form-control" v-model="form.ageBracketPrices[key]" :placeholder="basePlaceholder(key)">
              </td>
            </tr>
          </tbody>
        </table>
        <small class="form-text text-muted">{{ $t('pricePeriods.bracketsHelp') }}</small>
      </div>
      <div v-else class="form-group">
        <label>{{ $t('experiences.price') }} (€)*</label>
        <input type="number" step="0.01" min="0" class="form-control" v-model="form.price" placeholder="0.00">
      </div>
      <template #footer>
        <CButton color="secondary" @click="showForm = false">{{ $t('cancel') }}</CButton>
        <CButton color="primary" @click="save" :disabled="saving">
          <CSpinner v-if="saving" size="sm" class="mr-1" />
          {{ $t('save') }}
        </CButton>
      </template>
    </CModal>

    <!-- Delete period -->
    <CModal :show.sync="showDelete" :title="$t('confirmDelete')" color="primary">
      <div v-if="deleteErrors.length" class="alert alert-danger" role="alert">
        <div v-for="(message, index) in deleteErrors" :key="index">{{ message }}</div>
      </div>
      <p class="mb-0">{{ $t('pricePeriods.confirmDelete', { name: periodToDelete ? periodToDelete.name : '' }) }}</p>
      <template #footer>
        <CButton color="secondary" @click="showDelete = false">{{ $t('cancel') }}</CButton>
        <CButton color="primary" @click="confirmDelete" :disabled="deleting">
          <CSpinner v-if="deleting" size="sm" class="mr-1" />
          {{ $t('delete') }}
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import { pricePeriodsService, pricePeriodErrorMessages } from '@/services/pricePeriods.service';
import { AGE_BRACKET_KEYS, ageBracketLabel, periodColor, toIsoDate, formatDateDMY, formatEuro, formatBracketPrice, pricedAgeBrackets } from '@/helpers/prices';

// A blank bracket is left out of the period, so it keeps the base price
function isBlank(value) {
  return value === '' || value === null || value === undefined;
}

function emptyForm() {
  const ageBracketPrices = {};
  AGE_BRACKET_KEYS.forEach(key => { ageBracketPrices[key] = ''; });
  return { name: '', validFrom: '', validTo: '', price: 0, ageBracketPrices };
}

export default {
  name: 'ExperiencePricePeriods',
  props: {
    experienceId: {
      type: [String, Number],
      required: true
    },
    priceType: {
      type: String,
      default: 'PerExperience'
    },
    // The experience's own age-bracket prices, shown as placeholders
    baseAgeBracketPrices: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      ageBracketKeys: AGE_BRACKET_KEYS,
      periods: [],
      loading: false,
      loadError: '',
      showForm: false,
      editing: null,
      form: emptyForm(),
      formErrors: [],
      saving: false,
      showDelete: false,
      periodToDelete: null,
      deleteErrors: [],
      deleting: false,
      // periodId -> { from, to, days } (days with time slots from today to the period's end), { ended } or { failed }
      coverage: {}
    };
  },
  watch: {
    // The experience form locks the price type while periods exist
    periods(value) {
      this.$emit('count-changed', value.length);
    },
    // The form is reused when only ?id= changes
    experienceId() {
      this.showForm = false;
      this.showDelete = false;
      this.periods = [];
      this.load();
    }
  },
  mounted() {
    this.load();
    // Slots are created in another tab ("Criar horários"): refresh the coverage when the admin comes back
    window.addEventListener('focus', this.loadCoverage);
  },
  beforeDestroy() {
    window.removeEventListener('focus', this.loadCoverage);
  },
  methods: {
    ageBracketLabel,
    formatDateDMY,
    formatEuro,
    formatBracketPrice,
    pricedAgeBrackets,
    basePlaceholder(key) {
      const base = parseFloat(this.baseAgeBracketPrices[key]);
      return base > 0 ? this.$t('pricePeriods.basePricePlaceholder', { price: formatEuro(base) }) : this.$t('pricePeriods.noBasePrice');
    },
    keepsSomeBasePrices(period) {
      const overridden = period.ageBracketPrices || {};
      return AGE_BRACKET_KEYS.some(key => parseFloat(this.baseAgeBracketPrices[key]) > 0 && isBlank(overridden[key]));
    },
    colorOf(index) {
      return periodColor(index);
    },
    isSingleDay(period) {
      return toIsoDate(period.validFrom) === toIsoDate(period.validTo);
    },
    async load() {
      this.loading = true;
      this.loadError = '';
      try {
        const experienceId = this.experienceId;
        const periods = await pricePeriodsService.getList(experienceId);
        if (experienceId !== this.experienceId) return;
        this.periods = (Array.isArray(periods) ? periods : [])
          .slice()
          .sort((a, b) => toIsoDate(a.validFrom).localeCompare(toIsoDate(b.validFrom)));
        this.loadCoverage();
      } catch (error) {
        this.loadError = pricePeriodErrorMessages(error, this.$t('pricePeriods.loadError')).join(' ');
      } finally {
        this.loading = false;
      }
    },
    // Counts, per period, the days that have time slots between today and the period's end
    async loadCoverage() {
      const experienceId = this.experienceId;
      const today = toIsoDate(new Date());
      const coverage = {};
      await Promise.all(this.periods.map(async period => {
        const from = toIsoDate(period.validFrom) > today ? toIsoDate(period.validFrom) : today;
        let to = toIsoDate(period.validTo);
        if (to < today) {
          coverage[period.id] = { ended: true };
          return;
        }
        // The schedule endpoint serves at most 731 days
        const maxTo = new Date(from + 'T00:00:00');
        maxTo.setDate(maxTo.getDate() + 730);
        if (to > toIsoDate(maxTo)) to = toIsoDate(maxTo);
        try {
          const schedule = await pricePeriodsService.getSchedule(experienceId, from, to);
          const days = (schedule && schedule.days ? schedule.days : []).filter(day => day.slotCount > 0).length;
          coverage[period.id] = { from, to: toIsoDate(period.validTo), days };
        } catch (error) {
          coverage[period.id] = { failed: true };
        }
      }));
      if (experienceId === this.experienceId) this.coverage = coverage;
    },
    // New time-slot interval for this experience, prefilled with the period's remaining dates
    createSlotsHref(period) {
      const range = this.coverage[period.id] || {};
      return this.$router.resolve({
        name: 'AvailabilityIntervalsAdd',
        query: { experienceId: String(this.experienceId), startDate: range.from, endDate: range.to }
      }).href;
    },
    openSchedule() {
      this.$router.push({ name: 'ExperiencesPriceSchedule', query: { id: this.experienceId } });
    },
    openAdd() {
      this.editing = null;
      this.form = emptyForm();
      this.formErrors = [];
      this.showForm = true;
    },
    openEdit(period) {
      const form = emptyForm();
      form.name = period.name;
      form.validFrom = toIsoDate(period.validFrom);
      form.validTo = toIsoDate(period.validTo);
      form.price = period.price || 0;
      if (period.ageBracketPrices) {
        // Only the brackets the period changes; the rest stay blank (base price)
        Object.keys(period.ageBracketPrices).forEach(key => {
          if (!isBlank(period.ageBracketPrices[key])) form.ageBracketPrices[key] = period.ageBracketPrices[key];
        });
      }
      this.editing = period;
      this.form = form;
      this.formErrors = [];
      this.showForm = true;
    },
    validate() {
      const errors = [];
      if (!this.form.name || !this.form.name.trim()) errors.push(this.$t('pricePeriods.nameRequired'));
      if (!this.form.validFrom || !this.form.validTo) errors.push(this.$t('pricePeriods.datesRequired'));
      else if (this.form.validFrom > this.form.validTo) errors.push(this.$t('pricePeriods.datesOrder'));
      if (this.priceType === 'PerPerson') {
        const prices = AGE_BRACKET_KEYS
          .filter(key => !isBlank(this.form.ageBracketPrices[key]))
          .map(key => parseFloat(this.form.ageBracketPrices[key]));
        if (prices.some(price => price < 0)) errors.push(this.$t('pricePeriods.priceNegative'));
        else if (!prices.length) errors.push(this.$t('pricePeriods.priceRequired'));
      } else if (this.form.price === '' || this.form.price === null || parseFloat(this.form.price) < 0) {
        errors.push(this.$t('pricePeriods.priceNegative'));
      }
      return errors;
    },
    buildPayload() {
      const payload = {
        name: this.form.name.trim(),
        validFrom: this.form.validFrom,
        validTo: this.form.validTo,
        price: 0,
        ageBracketPrices: {}
      };
      if (this.priceType === 'PerPerson') {
        AGE_BRACKET_KEYS.forEach(key => {
          // Blank: left out (base price); a value, including 0 (free), is sent as is
          if (!isBlank(this.form.ageBracketPrices[key])) payload.ageBracketPrices[key] = parseFloat(this.form.ageBracketPrices[key]);
        });
      } else {
        payload.price = parseFloat(this.form.price) || 0;
      }
      return payload;
    },
    async save() {
      this.formErrors = this.validate();
      if (this.formErrors.length) return;
      this.saving = true;
      try {
        const payload = this.buildPayload();
        if (this.editing) {
          await pricePeriodsService.update(this.experienceId, this.editing.id, payload);
        } else {
          await pricePeriodsService.create(this.experienceId, payload);
        }
        this.showForm = false;
        this.$store.dispatch('alert/setAlert', { type: 'success', message: this.$t('pricePeriods.saveSuccess') });
        await this.load();
      } catch (error) {
        this.formErrors = pricePeriodErrorMessages(error, this.$t('pricePeriods.saveError'));
      } finally {
        this.saving = false;
      }
    },
    openDelete(period) {
      this.periodToDelete = period;
      this.deleteErrors = [];
      this.showDelete = true;
    },
    async confirmDelete() {
      if (!this.periodToDelete) return;
      this.deleting = true;
      try {
        await pricePeriodsService.delete(this.experienceId, this.periodToDelete.id);
        this.showDelete = false;
        this.periodToDelete = null;
        this.$store.dispatch('alert/setAlert', { type: 'success', message: this.$t('pricePeriods.deleteSuccess') });
        await this.load();
      } catch (error) {
        this.deleteErrors = pricePeriodErrorMessages(error, this.$t('pricePeriods.deleteError'));
      } finally {
        this.deleting = false;
      }
    }
  }
};
</script>

<style scoped>
.price-periods {
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #dee2e6;
}

.period-coverage {
  margin-top: 0.25rem;
  white-space: normal;
}

.period-no-slots {
  font-size: 0.8rem;
  color: #8a6d3b;
  background: #fcf8e3;
  border-radius: 4px;
  padding: 0.35rem 0.5rem;
  max-width: 28rem;
  margin: 0 auto;
  text-align: center;
}

.price-periods-empty {
  padding: 1rem;
  border: 1px dashed #c4c9d0;
  border-radius: 4px;
  color: #768192;
  text-align: center;
}

.price-periods td {
  vertical-align: middle;
}

.period-swatch {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  margin-right: 0.5rem;
}

.bracket-price {
  display: inline-block;
  margin-right: 0.75rem;
  white-space: nowrap;
}
</style>
